import axios from 'axios';
import {useParams} from 'react-router';
import {useSelector} from 'react-redux';
import {Grid} from '@material-ui/core';
import {FormattedMessage, useIntl} from 'react-intl';
import React, {useMemo, useState, useEffect, useCallback} from 'react';

import {Header} from './Header';
import {IJobs} from '../contexts/JobsDto';
import URL_CONST from '../../../config/url';
import {FILE_TYPE, VIEW_TYPE} from '../const';
import {forEach, isEmpty, isEqual} from 'lodash';
import {FileViewer} from '../FileViewer/FileViewer';
import {BusyOverlay, ListViewer} from './ListViewer';
import {VideoConfigDialog} from './VideoConfigDialog';
import {JobData} from '../../../types/dataService/Job';
import {Status} from '../../../types/operation/Status';
import {RootState, useAppDispatch} from '../../../store';
import {useVideoPreview} from './hooks/useVideoPreview';
import {toast} from '../../../base-components/StudioToast';
import {dataPrepareActions} from '../../../store/dataPrepare';
import {useDataSetJobs} from '../contexts/DataSetJobsContext';
import Waiting from '../../../base-components/StudioWaiting/Waiting';
import {addListener, removeListener} from '../../../store/statusChecker';
import {deleteDatasetMetadataByBatchId} from '../../../store/dataService';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import {cancel as cancelJob, finalize} from '../../../store/dataService/job';
import {RSQLProps} from '../../../base-components/StudioDataset/DatasetLoader';
import {StudioContentLayout} from '../../../base-components/StudioContentLayout';
import {DataPreparationOrigin} from '../../../types/dataset/DataPreparationContainer';
import {METADATA_IMAGE, METADATA_VIDEO} from '../../../types/dataService/DatasetMetadata';
import {StudioDatasetViewer} from '../../../base-components/StudioDataset/StudioDatasetViewer';
import {useStudioDatasetViewerContext} from '../../../base-components/StudioDataset/StudioDatasetViewerProvider';
import {
  RendererProps,
  BinaryRenderer,
} from '../../../base-components/StudioDataset/Renderer';
import {
  ViewId,
  ItemType,
  FilterProps,
} from '../../../base-components/StudioDataset/FileViewer';

import './DataReview.scss';

const RSQL_LABEL = [
  {id: 'binary/video', rsql: 'type==binary/video'},
  // uploaded images
  {id: 'binary/image', rsql: 'type==binary/image'},
];
const RSQL_LIST = [{id: 'image&video', rsql: 'type=in=(binary/image,binary/video)'}];

export type BinaryJobMap = {[key: string]: JobData[]};
export type LabelHeaderMap = {[key: string]: string};
export type BinaryFileMap = {[key: string]: ItemType};

const KFD_STATUS_EVENT_ID = 'KFD';

type DataReviewProps = {
  isEditing?: boolean;
  origin?: DataPreparationOrigin;
};

export const DataReview = ({isEditing = false, origin = 'raw'}: DataReviewProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const {setStatus} = dataPrepareActions;
  const {projectId} = useParams<{projectId: string}>();
  const {addDataSetJobsSubscriber, removeDataSetJobsSubscriber} = useDataSetJobs();

  const [isKFDLoading, setIsKFDLoading] = useState(false);

  const [view, setView] = useState<ViewId>(VIEW_TYPE.LABEL);
  const [search, setSearch] = useState<string | undefined>();

  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const [viewfilter, setViewFilter] = useState<RSQLProps[] | undefined>();
  const [filter, setFilter] = useState<RSQLProps[] | undefined>(RSQL_LABEL);

  const [cancelJobId, setCancelJobId] = useState<string | null>(null);

  const [labelHeader, setLabelHeader] = useState<LabelHeaderMap>({});
  const [videoBinaries, setVideoBinaries] = useState<BinaryFileMap>({});

  const dataset = useSelector((state: RootState) => state.dataset.data);
  const datasetId = useMemo(() => dataset?.id, [dataset]);

  const [busyBinaries, setBusyBinaries] = useState<string[]>([]);

  const [jobs, setJobs] = useState<IJobs>();
  const [jobsIdsToFinalize, setJobsIdsToFinalize] = useState<string[]>([]);
  const [videoSelection, setVideoSelection] = useState<string | undefined>();
  const videoData = useSelector((state: RootState) => state.dataPrepare.video);
  const {status, refreshBinaries} = useStudioDatasetViewerContext();

  const {generateVideoPreview, processedPreviewsVideosBinariesIds} = useVideoPreview({
    datasetId,
    jobs,
  });

  const LABEL_HEADERS: LabelHeaderMap = useMemo(
    () => ({
      'binary/video': intl.formatMessage({
        id: 'general.title.video',
      }),
      'binary/image': intl.formatMessage({
        id: 'general.title.image',
      }),
    }),
    [intl]
  );

  const annotationData = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );
  const imagesUploadedToDataPreparationContainer = useMemo(
    () =>
      annotationData?.images?.map(image => ({
        id: image.id.toString(),
        name: image.file_name,
        size: image.size,
        label: intl.formatMessage({id: 'dataPrep.review.unmutable.files.group'}),
        binaryType: 'binary/image',
        type: 'image',
        height: image.height,
        width: image.width,
        selected: false,
        lastModified: new Date(image.date_captured),
      })) || [],
    [intl, annotationData]
  );

  const combineFilters = useCallback(() => {
    setFilter(
      isEmpty(search)
        ? viewfilter
        : viewfilter?.map(filter => ({
            id: filter.id,
            rsql: filter.rsql.concat(`;${searchFilter} `),
          }))
    );
  }, [search, searchFilter, viewfilter]);

  useEffect(
    function setOriginalPathIfSearchChanges() {
      setSearchFilter(`originalPath=re=.*${search}`);
    },
    [search]
  );

  useEffect(
    function combineFiltersIFSearchChanged() {
      combineFilters();
    },
    [combineFilters]
  );

  const handleKFDStatusUpdate = useCallback(
    (status: Status) => {
      if (status.finished) {
        dispatch(removeListener(KFD_STATUS_EVENT_ID));

        axios
          .post(`${URL_CONST.DATASET_KFD_FINALIZE}?projectId=${projectId}`)
          .finally(() => {
            setIsKFDLoading(false);
            refreshBinaries();
          })
          .catch(error => {
            toast.error(error.message);
          });
      }
    },
    [dispatch, refreshBinaries, projectId]
  );

  useEffect(() => {
    dispatch(removeListener(KFD_STATUS_EVENT_ID));

    if (isKFDLoading) {
      dispatch(addListener(KFD_STATUS_EVENT_ID, handleKFDStatusUpdate));
    }

    return () => {
      dispatch(removeListener(KFD_STATUS_EVENT_ID));
    };
  }, [dispatch, handleKFDStatusUpdate, isKFDLoading]);

  useMemo(() => {
    if (view === VIEW_TYPE.LABEL) {
      setViewFilter(RSQL_LABEL);
      setLabelHeader(LABEL_HEADERS);
    } else {
      setViewFilter(RSQL_LIST);
    }
    // eslint-disable-next-line
  }, [view]);

  // Generate Label Header
  useMemo(() => {
    // Add default video view settings
    const header: LabelHeaderMap = {
      'binary/video': LABEL_HEADERS['binary/video'],
    };
    // Add default image at the last
    header['binary/image'] = intl.formatMessage({
      id: 'general.title.image',
    });
    setLabelHeader(header);
    // eslint-disable-next-line
  }, [videoBinaries]);

  // Bubble up viewer status to app level
  useMemo(() => dispatch(setStatus(status)), [status, setStatus, dispatch]);

  const onFilter = useCallback(
    (localFilter: FilterProps | null, subView: boolean) => {
      if (subView && localFilter) {
        let rsql;
        if (localFilter.id !== METADATA_IMAGE && localFilter.id !== METADATA_VIDEO) {
          const idParam = localFilter.id.split('/').pop();

          rsql = idParam && `type==${METADATA_IMAGE};`;
        } else {
          rsql = `type=in=(${localFilter.id})`.concat(
            isEmpty(search) ? '' : `;${searchFilter}`
          );
        }

        if (rsql) {
          const subViewFilter: RSQLProps = {
            id: localFilter.label,
            rsql,
          };

          setFilter([subViewFilter]);
        }
      } else {
        combineFilters();
      }
    },
    [search, searchFilter, combineFilters]
  );

  const onClick = ({type, url, id, ...rest}: ItemType) => {
    if (type === FILE_TYPE.VIDEO) {
      if (busyBinaries.includes(id)) {
        // Cancel current job on the binary
        setCancelJobId(id);
      } else {
        setVideoSelection(id);
      }
    }
  };

  const onDataChange = (files: ItemType[]) => {
    const videoMap: BinaryFileMap = {};
    forEach(files, file => {
      if (file.binaryType === METADATA_VIDEO) {
        videoMap[file.id] = file;
      }
    });
    !isEqual(videoMap, videoBinaries) && setVideoBinaries(videoMap);
  };

  const cancelImageGeneration = useCallback(
    async (binaryId: string) => {
      const videoSamplingJobs = jobs?.getOnGoingJobs().getJobsByBinaryId(binaryId);

      setCancelJobId(null);

      if (videoSamplingJobs?.length && datasetId) {
        videoSamplingJobs.getJobInstancesArray().forEach(async job => {
          const jobId = job.getId();

          await dispatch(cancelJob({datasetId, jobId}));
          setJobsIdsToFinalize(prevIds => [...prevIds, jobId]);

          dispatch(
            deleteDatasetMetadataByBatchId({
              datasetId,
              batchIds: job.getAssociatedGroupUploadedBatchId(),
            })
          );
        });
      }
    },
    [dispatch, datasetId, jobs]
  );

  useEffect(
    function finalizeJobs() {
      if (jobsIdsToFinalize.length && datasetId) {
        const finalizedJobsIds: string[] = [];

        jobsIdsToFinalize.forEach(jobId => {
          if (jobs?.getFinishedJobs().getJobById(jobId)) {
            finalizedJobsIds.push(jobId);
            dispatch(finalize({datasetId, jobId}));
          }
        });

        setJobsIdsToFinalize(prevIds => {
          return prevIds.filter(jobId => !finalizedJobsIds.includes(jobId));
        });
      }
    },
    [jobsIdsToFinalize, datasetId, jobs, dispatch]
  );

  useEffect(
    function updateLastJobsStatusUpdate() {
      function subscriber(jobs: IJobs) {
        setJobs(jobs);
      }

      if (datasetId) {
        addDataSetJobsSubscriber(datasetId, subscriber);
      }

      return () => {
        if (datasetId) {
          removeDataSetJobsSubscriber(datasetId, subscriber);
        }
      };
    },
    [datasetId, addDataSetJobsSubscriber, removeDataSetJobsSubscriber]
  );

  useEffect(
    function updateBusyBinaries() {
      if (jobs) {
        const onGoingJobs = jobs.getOnGoingJobs();
        const newBusyBinaries = onGoingJobs.getUniquesBinariesIds();
        const hasDifferences =
          JSON.stringify(newBusyBinaries) !== JSON.stringify(busyBinaries);

        if (hasDifferences) {
          setBusyBinaries(newBusyBinaries);

          if (datasetId && onGoingJobs.length === 0) {
            refreshBinaries();
          }
        }
      }
    },
    [jobs, busyBinaries, refreshBinaries, datasetId]
  );

  return (
    <div className="data-review">
      {isKFDLoading ? (
        <div className="loading-container">
          <Waiting
            className="waiter-container"
            diameter={100}
            loadingText={intl.formatMessage({
              id: 'dataPrep.kfd.running',
            })}
          />
          <FormattedMessage tagName="p" id="dataPrep.kfd.running.subtext" />
        </div>
      ) : (
        <StudioContentLayout
          className="data-review__content"
          header={
            <>
              <h4>
                <FormattedMessage
                  id={isEditing ? 'dataPrep.edit.files' : 'dataPrep.review.files'}
                />
              </h4>
              <Header
                view={view}
                onView={setView}
                search={search}
                onSearch={setSearch}
                dataContainerImagesQuantity={
                  imagesUploadedToDataPreparationContainer.length
                }
              />
            </>
          }
        >
          {({width, contentHeight}) => (
            <Grid container>
              <Grid
                container
                direction="column"
                className="data-review__new-files-container"
              >
                <Grid container alignContent="flex-start">
                  <FormattedMessage tagName="h4" id="dataPrep.review.new.files.title" />
                </Grid>

                <StudioDatasetViewer
                  dataset={dataset}
                  datasetVersion={dataset?.version}
                  width={width}
                  height={contentHeight}
                  showLabels={false}
                  readOnly={false}
                  view={view}
                  filter={filter}
                  labelHeader={labelHeader}
                  groupByKey="binaryType"
                  allowMultiselection
                  busyItems={busyBinaries}
                  listViewRender={ListViewer}
                  itemRenderer={(props: GridRendererProps) => {
                    const url = props.url;
                    const binaryId = props.item.id;
                    const isVideoType = props.item.type === FILE_TYPE.VIDEO;
                    let thumbnailUrl = null;
                    let isLoadingThumbnail = false;

                    if (url) {
                      thumbnailUrl =
                        typeof url === 'string' ? url : URL.createObjectURL(url);
                    }

                    if (thumbnailUrl && binaryId && isVideoType) {
                      const alreadyProcessedBinaryPreview = processedPreviewsVideosBinariesIds.includes(
                        props.item.id
                      );

                      const queryString = thumbnailUrl.split('?')[1] || '';
                      const urlParams = new URLSearchParams(queryString);

                      urlParams.set('previewDimensions', '210x140');
                      urlParams.set('animated', 'true');

                      thumbnailUrl = `${
                        thumbnailUrl.split('?')[0]
                      }?${urlParams.toString()}`;

                      if (!alreadyProcessedBinaryPreview) {
                        generateVideoPreview(props.item.id, thumbnailUrl);
                        isLoadingThumbnail = true;
                      }
                    }

                    return (
                      <GridRenderer
                        {...props}
                        url={thumbnailUrl}
                        isLoadingThumbnail={isLoadingThumbnail}
                        onCancel={id => setCancelJobId(id)}
                      />
                    );
                  }}
                  onFilter={onFilter}
                  onClick={onClick}
                  onDataChange={onDataChange}
                />
              </Grid>

              {imagesUploadedToDataPreparationContainer.length === 0 ? null : (
                <Grid container direction="column">
                  <Grid container alignContent="flex-start">
                    <FormattedMessage
                      tagName="h4"
                      id="dataPrep.review.unmutable.files.title"
                    />
                  </Grid>
                  <FileViewer
                    view={view}
                    origin={origin}
                    projectId={projectId}
                    items={imagesUploadedToDataPreparationContainer}
                    className="data-review__dataContainer__file-viewer"
                    goBackLabel={intl.formatMessage({id: 'dataset.viewer.go.back'})}
                  />
                </Grid>
              )}
            </Grid>
          )}
        </StudioContentLayout>
      )}

      {/* Need to externalize */}
      <VideoConfigDialog
        open={!!videoSelection}
        datasetId={dataset?.id || ''}
        binaryId={videoSelection || ''}
        data={videoSelection ? videoData[videoSelection] : []}
        onKFDSubmit={() => {
          setIsKFDLoading(true);
        }}
        onClose={() => setVideoSelection(undefined)}
      />
      {/* Cancel Video Image extractioni */}
      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage({id: 'dataPrep.video.cancel.sample.title'})}
        message={intl.formatMessage({id: 'dataPrep.video.cancel.sample.desc'})}
        open={!!cancelJobId}
        onClose={() => setCancelJobId(null)}
        onOk={() => {
          if (cancelJobId) {
            cancelImageGeneration(cancelJobId);
          }
        }}
        cancelLabel={intl.formatMessage({id: 'form.resume'})}
        submitLabel={intl.formatMessage({id: 'form.cancel'})}
      />
    </div>
  );
};

// Binary Renderer with cancel button
export type GridRendererProps = RendererProps & {
  isBusy?: boolean;
  progress?: number;
  onCancel?: (id: string) => void;
};

export const GridRenderer = ({
  isBusy,
  progress = 0,
  onCancel = () => {},
  isLoadingThumbnail = false,
  item,
  ...rest
}: GridRendererProps) => {
  return (
    <BinaryRenderer
      {...rest}
      item={item}
      isBusy={isBusy}
      isLoadingThumbnail={isLoadingThumbnail}
      className="grid-item"
      overlayElement={
        <BusyOverlay
          item={item}
          progress={progress}
          hideCloseButton={isLoadingThumbnail}
          isBusy={isBusy || isLoadingThumbnail}
          onClick={el => onCancel(item.id)}
        />
      }
    />
  );
};
